(() => {
  if (customElements.get('nw-companion')) return;
  const ROOT = window.Shopify?.routes?.root || '/';
  const PREFIX = '/apps/nordweg-welcome';
  const KEY = 'nw_offer_journey_v1';
  const money = cents => new Intl.NumberFormat('en-US', {style:'currency',currency:'USD'}).format(cents / 100);
  const numericId = value => String(value || '').split('/').pop();
  const gid = id => `gid://shopify/Product/${id}`;
  let queue = Promise.resolve();
  let handoffs = Promise.resolve();
  let cached = null;
  function state() {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY));
      if (saved && saved.expires > Date.now()) {
        if (!cached) cached = saved;
        else if (cached.id && saved.id === cached.id) {
          cached.declined = [...new Set([...cached.declined,...(saved.declined || [])])];
          if (saved.prepaymentPresented) cached.prepaymentPresented = true;
          if (saved.prepaymentResolved) cached.prepaymentResolved = true;
          if (!cached.prepaymentOfferId) cached.prepaymentOfferId = saved.prepaymentOfferId;
        } else if (cached.id && /^[a-f0-9]{32}$/.test(saved.id || '')) cached.journeyConflict = true;
      }
    } catch { /* Storage is optional. */ }
    return cached || (cached = {id:'',declined:[],expires:Date.now()+30*86400000});
  }
  function save() {
    const current = state();
    if (current.journeyConflict) return;
    try { localStorage.setItem(KEY, JSON.stringify(current)); } catch { /* Keep this tab's suppression state. */ }
  }
  function mergeCartState(cart) {
    const current = state(), remoteId = cart.attributes?.nw_offer_journey;
    if (current.journeyConflict) return false;
    if (current.id && /^[a-f0-9]{32}$/.test(remoteId || '') && remoteId !== current.id) {
      // An older tab must never replace another tab's current cart journey.
      current.journeyConflict = true; return false;
    }
    if (!state().id && /^[a-f0-9]{32}$/.test(cart.attributes?.nw_offer_journey || '')) state().id = cart.attributes.nw_offer_journey;
    if (!state().id || cart.attributes?.nw_offer_journey !== state().id) return true;
    if (cart.attributes.nw_offer_prepaid_presented === '1') state().prepaymentPresented = true;
    if (cart.attributes.nw_offer_prepaid_resolved === '1') state().prepaymentResolved = true;
    const offerId = cart.attributes.nw_offer_prepaid_offer;
    if (typeof offerId === 'string' && /^[A-Za-z0-9-]{1,80}$/.test(offerId)) {
      if (state().prepaymentOfferId && state().prepaymentOfferId !== offerId) state().prepaymentResolved = true;
      else state().prepaymentOfferId = offerId;
    }
    const declined = String(cart.attributes.nw_offer_declined || '').split(',').filter(id=>/^[1-9]\d*$/.test(id)).slice(-20).map(gid);
    state().declined = [...new Set([...state().declined,...declined])]; save();
    return true;
  }
  function suppressed(offer) {
    return state().journeyConflict || state().prepaymentResolved || state().declined.includes(offer.offeredProductId)
      || (state().prepaymentOfferId && state().prepaymentOfferId !== offer.id);
  }
  async function request(url, body, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort(); else signal?.addEventListener('abort', abort, {once:true});
    const timer = setTimeout(abort, body?.items ? 10000 : 1900);
    try {
      const response = await fetch(url, {method:body?'POST':'GET',credentials:'same-origin',signal:controller.signal,
        ...(body?{headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}:{})});
      if (!response.ok) throw new Error('REQUEST_UNCONFIRMED');
      return await response.json();
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  }
  function safeOffer(offer) {
    if (!offer || !/^[1-9]\d*$/.test(numericId(offer.variantId)) || !/^[1-9]\d*$/.test(numericId(offer.offeredProductId))
      || offer.currencyCode !== 'USD' || !Number.isSafeInteger(offer.priceCents) || offer.priceCents <= 0) return false;
    try { const url = new URL(offer.productUrl); return url.origin === 'https://nordweg.us' && url.pathname.startsWith('/products/'); } catch { return false; }
  }
  function element(tag, text, className) {
    const node = document.createElement(tag); if (text) node.textContent = text; if (className) node.className = className; return node;
  }
  async function persist() {
    const attributes = () => ({nw_offer_journey:state().id,nw_offer_declined:state().declined.map(numericId).slice(-20).join(','),nw_offer_prepaid_presented:state().prepaymentPresented?'1':'',
      nw_offer_prepaid_offer:state().prepaymentOfferId || '',nw_offer_prepaid_resolved:state().prepaymentResolved?'1':''});
    const differs = (cart, desired) => Object.keys(desired).some(key => (cart.attributes?.[key] || '') !== desired[key]);
    const operation = async () => {
      let cart = await request(`${ROOT}cart.js`);
      if (!mergeCartState(cart)) throw new Error('JOURNEY_CHANGED');
      for (let attempt = 0; attempt < 2; attempt++) {
        const desired = attributes();
        if (!desired.nw_offer_journey || !differs(cart, desired)) return;
        await request(`${ROOT}cart/update.js`, {attributes:desired});
        cart = await request(`${ROOT}cart.js`);
        if (!mergeCartState(cart)) throw new Error('JOURNEY_CHANGED');
        const latest = attributes();
        if (!differs(cart, latest)) return;
        // A decline can arrive while Shopify saves an earlier presentation.
        // Repair that stale save before exposing or accepting the offer.
        if (Object.keys(desired).every(key => desired[key] === latest[key])) break;
      }
      throw new Error('JOURNEY_HANDOFF_UNCONFIRMED');
    };
    const run = () => navigator.locks?.request ? navigator.locks.request('nw-offer-cart-handoff', operation) : operation();
    const pending = handoffs.then(run, run); handoffs = pending.catch(()=>{});
    return pending;
  }
  async function refreshCart() {
    const main = document.getElementById('main-cart-footer')?.dataset.id;
    const ids = ['cart-drawer','cart-icon-bubble',...(main?[main]:[])];
    const url = new URL(window.location.href); url.search = ''; url.searchParams.set('sections',ids.join(','));
    const sections = await request(url.toString());
    const drawer = document.querySelector('cart-drawer');
    if (!sections || (main && !sections[main]) || (drawer && !sections['cart-drawer']) || (!main && !drawer))
      throw new Error('CART_SECTIONS_UNCONFIRMED');
    const cart = await request(`${ROOT}cart.js`); cart.sections = sections;
    if (drawer && drawer.renderContents?.(cart, {open:drawer.hasAttribute('open'),added:false}) !== true)
      throw new Error('CART_DRAWER_UNCONFIRMED');
    if (sections['cart-icon-bubble']) {
      const parsedIcon = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      document.querySelectorAll('[data-cart-icon-bubble]').forEach(link => {
        const content = parsedIcon.querySelector(`template[data-cart-icon-bubble-content="${link.dataset.cartIconBubble}"]`);
        if (content) link.replaceChildren(content.content.cloneNode(true));
      });
    }
    const cartItems = document.querySelector('cart-items');
    if (main && sections[main]) {
      // Reuse the theme's exact section replacement contract; no estimated totals.
      const parsed = new DOMParser().parseFromString(sections[main], 'text/html');
      for (const id of ['main-cart-items','main-cart-footer']) {
        const current = document.getElementById(id), incoming = parsed.getElementById(id);
        if (current && incoming) current.innerHTML = incoming.innerHTML;
      }
    }
    if (cartItems) cartItems.classList.toggle('is-empty', cart.item_count === 0);
    document.dispatchEvent(new CustomEvent('nw:offer-cart-updated'));
    return cart;
  }
  class Companion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled !== 'true' || this.started) return;
      this.started = true; this.pending = false; this.hidden = true;
      this.impressionIds = new Set();
      this.visibilityListener = () => {
        clearTimeout(this.impressionTimer);
        if (document.visibilityState === 'visible' && this.impressionObserver && this.offer?.id
          && !this.impressionIds.has(this.offer.id)) {
          this.impressionObserver.unobserve(this);
          this.impressionObserver.observe(this);
        }
      };
      document.addEventListener('visibilitychange', this.visibilityListener);
      this.refreshListener = () => this.schedule();
      this.changeListener = event => {
        if (this.dataset.placement === 'product' && !this.contains(event.target)) {
          const selected=document.getElementById(this.dataset.formId)?.querySelector('[name="id"]')?.value;
          if(this.anchor && String(this.anchor.variant_id)!==String(selected)){this.controller?.abort();this.clear();}
          this.schedule();
        }
      };
      document.addEventListener('nw:cart-add-confirmed', this.refreshListener);
      document.addEventListener('nw:offer-cart-updated', this.refreshListener);
      document.addEventListener('change', this.changeListener);
      if ('IntersectionObserver' in window) {
        // Observe a visible parent: the optional card stays hidden until it has an offer.
        this.observer = new IntersectionObserver(entries => { if (entries.some(e=>e.isIntersecting)) { this.observer.disconnect(); this.load(); } });
        this.observer.observe(this.parentElement);
      } else this.load();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect(); clearTimeout(this.timer);
      clearTimeout(this.revealTimer);
      this.stopImpression();
      document.removeEventListener('visibilitychange', this.visibilityListener);
      document.removeEventListener('nw:cart-add-confirmed',this.refreshListener);
      document.removeEventListener('nw:offer-cart-updated',this.refreshListener);
      document.removeEventListener('change',this.changeListener);
    }
    schedule() { clearTimeout(this.timer); this.timer = setTimeout(()=>this.load(),120); }
    async load() {
      if (this.pending || !this.isConnected) return;
      this.controller?.abort(); this.controller = new AbortController(); const controller = this.controller;
      let offerLookupTimer;
      try {
        const cart = await request(`${ROOT}cart.js`, null, controller.signal);
        if (cart.currency !== 'USD') { this.clear(); return; }
        if (!mergeCartState(cart)) { this.dataset.offerStatus = 'JOURNEY_CHANGED'; this.clear(); return; }
        if (state().prepaymentResolved || (state().prepaymentPresented && !state().prepaymentOfferId)) {
          this.dataset.offerStatus = 'PREPAYMENT_OFFER_PRESENTED'; this.clear(); return;
        }
        let anchors = [...cart.items].sort((a,b)=>b.final_line_price-a.final_line_price);
        if (this.dataset.placement === 'product') {
          const form = document.getElementById(this.dataset.formId);
          const variantId = form?.querySelector('[name="id"]')?.value || this.dataset.variantId;
          if (!/^[1-9]\d*$/.test(String(variantId))) { this.clear(); return; }
          const selected=JSON.parse(this.dataset.variants || '[]').find(variant=>String(variant.id)===String(variantId));
          anchors = [{product_id:Number(this.dataset.productId),variant_id:Number(variantId),variant_title:selected?.title || '',price:selected?.price,quantity:1}];
        }
        if (!anchors.length) { this.clear(); return; }
        const seen = new Set();
        anchors = anchors.filter(anchor => {const key=String(anchor.variant_id);if(seen.has(key))return false;seen.add(key);return true;});
        // Cart requests each have a 1900ms limit. Give a later cart item time
        // for several probes; this optional search never blocks checkout.
        offerLookupTimer = setTimeout(()=>{
          if (this.controller === controller) {this.dataset.offerStatus='OFFER_UNAVAILABLE';this.clear();}
          controller.abort();
        },8000);
        for (const anchor of anchors) {
          if (controller.signal.aborted) return;
          const result = await request(`${PREFIX}/offers`, {
            placement:this.dataset.placement,journeyId:state().id,cartToken:cart.token,
            anchorProductId:gid(anchor.product_id),anchorVariantId:`gid://shopify/ProductVariant/${anchor.variant_id}`,
            cartProductIds:[...new Set([...cart.items.map(item=>gid(item.product_id)),gid(anchor.product_id)])],
            cartVariantIds:[...new Set([...cart.items.map(item=>`gid://shopify/ProductVariant/${item.variant_id}`),`gid://shopify/ProductVariant/${anchor.variant_id}`])],
            declinedProductIds:state().declined,
            saleInCart:cart.items.some(item=>item.original_line_price>item.final_line_price),promotionPresent:cart.total_discount>0,
          }, controller.signal);
          if (result.journeyId) {
            state().id=result.journeyId; save();
          }
          this.dataset.offerStatus = result.reasonCode || 'NO_SUITABLE_COMPANION';
          if (controller.signal.aborted || !this.isConnected) return;
          if (result.reasonCode === 'CONTROL_HOLDOUT') { clearTimeout(offerLookupTimer); await persist().catch(()=>{}); this.clear(); return; }
          if (safeOffer(result.offer) && !state().declined.includes(result.offer.offeredProductId)
            && !cart.items.some(item=>String(item.product_id)===numericId(result.offer.offeredProductId))) {
            if (state().prepaymentOfferId && state().prepaymentOfferId !== result.offer.id) {
              this.dataset.offerStatus = 'PREPAYMENT_OFFER_PRESENTED'; this.clear(); return;
            }
            state().prepaymentOfferId = result.offer.id;
            state().prepaymentPresented = true; save();
            clearTimeout(offerLookupTimer);
            // Persist the handoff before displaying the offer. A failed event
            // cannot cause a second offer in checkout or a recovery link.
            await persist();
            if (controller.signal.aborted || !this.isConnected) return;
            if (suppressed(result.offer)) { this.dataset.offerStatus = 'PREPAYMENT_OFFER_PRESENTED'; this.clear(); return; }
            this.offer = result.offer; this.anchor = anchor; this.cart = cart; this.draw(); return;
          }
        }
        clearTimeout(offerLookupTimer);
        await persist().catch(()=>{});
        this.clear();
      } catch (error) {
        if (!controller.signal.aborted) { this.dataset.offerStatus = 'OFFER_UNAVAILABLE'; this.clear(); }
      } finally {clearTimeout(offerLookupTimer);}
    }
    stopImpression() { clearTimeout(this.impressionTimer); this.impressionObserver?.disconnect(); this.impressionObserver=null; }
    clear() { this.stopImpression(); this.hidden=true; this.replaceChildren(); }
    observeImpression() {
      this.stopImpression();
      if (!('IntersectionObserver' in window) || !this.offer?.id || this.impressionIds.has(this.offer.id)) return;
      const offerId=this.offer.id;
      this.impressionObserver=new IntersectionObserver(entries=>{
        clearTimeout(this.impressionTimer);
        if (document.visibilityState !== 'visible' || this.impressionIds.has(offerId)
          || !entries.some(entry=>entry.target===this && entry.isIntersecting && entry.intersectionRatio>=0.5)) return;
        this.impressionTimer=setTimeout(()=>{
          if (!this.isConnected || this.hidden || document.visibilityState !== 'visible' || this.offer?.id !== offerId) return;
          this.impressionIds.add(offerId);
          this.stopImpression();
          void this.event('impression').catch(()=>{});
        },1000);
      },{threshold:[0,0.5,1]});
      this.impressionObserver.observe(this);
    }
    draw() {
      const offer=this.offer; const productPage=this.dataset.placement==='product';
      const label=productPage?element('h3','COMPLETE YOUR CARRY','nw-companion__heading'):null;
      this.setAttribute('role','group');this.setAttribute('aria-label','Optional accessory');
      const row=element('div','','nw-companion__row');
      if (offer.imageUrl) {
        try { const url=new URL(offer.imageUrl); if(url.protocol==='https:'&&['cdn.shopify.com','nordweg.us'].includes(url.hostname)) { const img=element('img');img.src=url.href;img.alt=offer.imageAlt||offer.variantTitle;img.width=72;img.height=72;img.loading='lazy';row.append(img); } } catch { /* Exact image unavailable. */ }
      }
      const info=element('div','','nw-companion__info');const link=element('a',offer.offeredProductTitle,'nw-companion__product-title');link.href=offer.productUrl;
      info.append(link,element('p',`${offer.variantTitle} · ${money(offer.priceCents)}`,'nw-companion__variant'));
      row.append(info);
      const actions=element('div','','nw-companion__actions');const add=element('button');add.type='button';add.dataset.add='';
      const anchorInCart=this.cart.items.some(item=>String(item.variant_id)===String(this.anchor.variant_id));
      add.dataset.nwCommerceAction='add';add.dataset.nwCommerceKey=`companion-${this.dataset.placement}`;
      const plus=element('span','+','nw-commerce-action__icon');plus.setAttribute('aria-hidden','true');
      add.append(plus,element('span',productPage&&!anchorInCart?'ADD BOTH TO CART':'ADD TO CART'));
      add.addEventListener('click',()=>this.add());
      const decline=element('button','NOT NOW','nw-companion__decline');decline.type='button';decline.dataset.decline='';decline.addEventListener('click',()=>this.decline());
      actions.append(add,decline);
      const status=element('p','','nw-companion__status');status.setAttribute('role','status');status.setAttribute('aria-live','polite');
      const anchorLabel=productPage?element('p','','nw-companion__anchor'):null;
      if(anchorLabel)anchorLabel.append(element('span',this.dataset.productTitle,'nw-companion__product-title'),document.createTextNode(this.anchor.variant_title?` · ${this.anchor.variant_title}`:''));
      this.replaceChildren(...(label?[label]:[]),...(anchorLabel?[anchorLabel]:[]),row,actions,status);this.hidden=false;
      this.observeImpression();
    }
    async event(result) {
      if (!this.offer?.id || !state().id) return;
      return request(`${PREFIX}/offer-event`,{placement:this.dataset.placement,journeyId:state().id,offerId:this.offer.id,result});
    }
    async decline() {
      if (this.pending) return;
      this.stopImpression();
      state().declined=[...new Set([...state().declined,this.offer.offeredProductId])];state().prepaymentResolved=true;save();this.hidden=true;
      await this.event('declined').catch(()=>{});
      try { await persist(); } catch { /* Local suppression survives retries. */ }
    }
    async add() {
      if (this.pending || !this.offer || this.hidden || !this.isConnected) return;
      if (suppressed(this.offer)) { this.clear(); return; }
      this.stopImpression();
      this.pending=true;const button=this.querySelector('[data-add]');button.disabled=true;button.textContent='ADDING…';
      let confirmed=false, alreadyInCart=false;
      const operation=async()=>{
        const cart=await request(`${ROOT}cart.js`);const id=Number(numericId(this.offer.variantId));
        if (!mergeCartState(cart) || suppressed(this.offer)) { this.clear(); return; }
        const selected=document.getElementById(this.dataset.formId)?.querySelector('[name="id"]')?.value;
        if(this.dataset.placement==='product' ? String(selected)!==String(this.anchor.variant_id)
          : !cart.items.some(item=>String(item.variant_id)===String(this.anchor.variant_id)))throw new Error('ANCHOR_CHANGED');
        if (cart.items.some(item=>String(item.product_id)===numericId(this.offer.offeredProductId))) {
          state().prepaymentResolved=true;save();await persist().catch(()=>{});
          confirmed=true;alreadyInCart=true;await refreshCart();this.clear();return;
        }
        const items=[];
        if (this.dataset.placement==='product'&&!cart.items.some(item=>Number(item.variant_id)===this.anchor.variant_id)) items.push({id:this.anchor.variant_id,quantity:1});
        items.push({id,quantity:1,properties:{_nw_companion_rule:this.offer.ruleHandle || '',_nw_offer_id:this.offer.id}});
        let verified;
        try { await request(`${ROOT}cart/add.js`,{items}); }
        catch { /* A timeout can happen after Shopify adds the item: read before deciding. */ }
        verified=await request(`${ROOT}cart.js`);
        const complete=items.every(item=>verified.items.some(line=>Number(line.variant_id)===item.id&&Number(line.quantity)>=item.quantity));
        if(!complete)throw new Error('ADDITION_UNCONFIRMED');
        confirmed=true;
        state().prepaymentResolved=true;save();await persist().catch(()=>{});
        await this.event('accepted').catch(()=>{});await refreshCart();this.clear();
      };
      const run=()=>navigator.locks?.request?navigator.locks.request('nw-offer-cart-add',operation):operation();
      const pending=queue.then(run,run);queue=pending.catch(()=>{});
      try { await pending; }
      catch (error) {
        if(error?.message==='ANCHOR_CHANGED'){this.pending=false;this.clear();this.schedule();return;}
        if(confirmed){
          // Shopify has the item. If only the section refresh fails, offer a
          // full cart read without labeling the confirmed addition an error.
          if(this.isConnected){
            const status=this.querySelector('.nw-companion__status');
            if(status)status.textContent=alreadyInCart
              ? 'This item is already in your bag. View your bag for the current total.'
              : 'Added to your bag. View your bag for the updated total.';
            button.textContent='VIEW YOUR BAG';button.disabled=false;
            button.removeAttribute('data-add');button.dataset.nwCommerceAction='view';
            button.onclick=()=>{window.location.href=`${ROOT}cart`;};
          }
          this.pending=true;return;
        }
        state().declined=[...new Set([...state().declined,this.offer.offeredProductId])];save();
        await this.event('error').catch(()=>{});
        const status=this.querySelector('.nw-companion__status');
        if(status)status.textContent='We could not confirm this addition. Review your bag before trying again.';
        button.textContent='REVIEW YOUR BAG';button.disabled=false;button.onclick=()=>{window.location.href=`${ROOT}cart`;};
        // Preserve uncertainty: this button cannot submit another addition.
        this.pending=true;return;
      }
      this.pending=false;
    }
  }
  customElements.define('nw-companion',Companion);
})();
